export interface WorkflowStepEvent {
	type: string;
	step: string;
	status: 'starting' | 'completed' | 'error';
	message: string;
	timestamp: number;
	details?: any;
	context?: any;
}

export type StreamingEvent = WorkflowStepEvent | Record<string, any>;

export const parseStreamingLine = (line: string): StreamingEvent | null => {
	if (!line.trim() || line.trim() === '') {
		return null;
	}

	if (line.startsWith('data: ')) {
		const jsonStr = line.substring(6).trim();
		
		if (jsonStr === '[DONE]') {
			return null;
		}

		try {
			const parsed = JSON.parse(jsonStr);
			return parsed;
		} catch (error) {
			console.error('Failed to parse streaming line:', line, error);
			return null;
		}
	}

	return null;
};

export const isWorkflowEvent = (event: StreamingEvent): event is WorkflowStepEvent => {
	const validTypes = [
		'workflow_step',
		'rag_step',
		'langgraph_step',
		'question_normalization',
		'query_classification',
		'query_decomposition',
		'sql_generation',
		'table_analysis',
		'response_synthesis',
		'report_suggestions'
	];
	
	const isValid = !!(
		event.type && 
		validTypes.includes(event.type) &&
		'step' in event && 
		'status' in event &&
		'message' in event
	);
	
	return isValid;
};

export const isStreamingResponse = (event: StreamingEvent): boolean => {
	if (isToolResultEvent(event)) {
		return false;
	}
	return !!(
		event.type && 
		(event.type === 'main_result' || 
		event.type === 'insight' || 
		event.type === 'insights_complete')
	);
};

export const isToolResultEvent = (event: StreamingEvent): boolean => {
	return !!(event.type === 'main_result' && (('formatted_response' in event) || ('name' in event && 'data' in event)));
};

export const extractToolResult = (event: any) => {
	if (!isToolResultEvent(event)) {
		return null;
	}
	
	const queryResults = event.data;
	
	const transformedData = queryResults ? {
		headers: queryResults.columns || queryResults.headers || [],
		rows: queryResults.rows || [],
		...queryResults
	} : queryResults;
	
	const result = {
		type: (event.visualization || 'table').toLowerCase(),
		data: transformedData,
		sql_query: event.sql_query,
		question: event.question || event.original_question,
		metadata: event.metadata,
		timestamp: event.timestamp ? (typeof event.timestamp === 'number' && event.timestamp < 10000000000 ? event.timestamp * 1000 : event.timestamp) : Date.now(),
		report_suggestions: event.report_suggestions && Array.isArray(event.report_suggestions) ? event.report_suggestions : undefined,
		llm_payload: event.llm_payload,
	};

	return result;
};

export const extractStreamingStep = (event: WorkflowStepEvent) => {
	return {
		type: event.type,
		step: event.step,
		status: event.status,
		message: event.message,
		timestamp: event.timestamp,
		details: event.details,
		context: event.context,
	};
};

